import { useEffect } from "react";
import { motion } from "framer-motion";
import { MonitorCog, ArrowUpCircle, Puzzle, FileSearch, Handshake, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const erpServices = [
  {
    icon: MonitorCog,
    title: "Managed Services",
    desc: "We offer managed services including application monitoring, instance creation, performance tuning, administration, patching, and backups. Our proactive approach ensures optimal performance and minimal downtime.",
  },
  {
    icon: ArrowUpCircle,
    title: "Upgrades",
    desc: "We perform comprehensive upgrades for your business applications, whether partial, full, or with custom retrofits. Our upgrade solutions ensure your systems stay current and effective.",
  },
  {
    icon: Puzzle,
    title: "Specialty Services",
    desc: "Our specialty services encompass disaster recovery, mergers and acquisitions, data management, training, and licensing. We provide expert support to handle complex business scenarios.",
  },
  {
    icon: FileSearch,
    title: "Other Services",
    desc: "We offer ERP installation, implementation, migration, roll-out, data-only migration, and 24/7 support to ensure seamless operation and efficiency.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const BusinessApplications = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.businessApplications} jsonLd={[buildBreadcrumbJsonLd(seoConfig.businessApplications.crumbs)]} />
      <PageHero
        title="Business Applications"
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Technology Services", href: "/services/technology-services" },
          { label: "Business Applications" },
        ]}
        lightfall
      />

      <section data-testid="bizapps-intro" className="pt-24 lg:pt-12 pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Business Applications Services
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
              Optimizing Business Operations with Advanced Solutions
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              We offer a full suite of services designed to monitor, maintain, and upgrade a diverse range of
              business applications. Our goal is to ensure that your systems operate seamlessly and efficiently at
              all times. By providing proactive monitoring, regular maintenance, and strategic upgrades, we help
              minimize downtime, optimize performance, and extend the lifespan of your applications.
            </p>
            <p className="mt-4 text-base md:text-lg text-slate-500 leading-relaxed">
              Our comprehensive approach covers every aspect of application management, so you can focus on your
              core business activities while we take care of your technological infrastructure.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/Business.jpg"
                alt="Business Applications"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="erp-services" className="py-24 lg:py-16 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Streamlined ERP Management
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              ERP Application Services
            </h2>
            <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
            <p className="mt-5 text-base md:text-lg text-slate-500">
              Our ERP services include installation, implementation, migration, and 24/7 support. We ensure seamless
              integration and operation of your ERP systems to enhance business efficiency.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            {erpServices.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                data-testid={`erp-card-${i + 1}`}
                className="flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-8 hover:border-miti-blue/40 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center shrink-0">
                  <s.icon size={22} strokeWidth={1.8} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-slate-900">{s.title}</h3>
                  <p className="mt-2.5 text-sm md:text-base text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="bizapps-expertise" className="relative py-28 lg:py-36 overflow-hidden">
        <img
          src="/assets/bg-1.jpg"
          alt="Mastering the Latest Technologies"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-miti-navy/75" />
        <motion.div {...fadeUp} className="relative max-w-3xl mx-auto px-6 text-center">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-sky-300">
            Our Technology Expertise
          </span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
            Mastering the Latest Technologies
          </h2>
          <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
          <p className="mt-6 text-base md:text-lg text-slate-200 leading-relaxed">
            We specialize in deploying and managing applications at the intersection of cloud technology, data
            management, and process automation. Our expertise ensures robust and scalable solutions for your
            business needs.
          </p>
        </motion.div>
      </section>

      <section data-testid="bizapps-cta" className="py-24 lg:py-16 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Start a Conversation
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Ready to Enhance Your Business Applications?
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Connect with our experts today to discover how our business application services can optimize your
              operations and drive success.
            </p>
            <a
              href="/contact"
              data-testid="bizapps-contact-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Handshake size={18} />
              Contact Us
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/cta-bg.jpg"
                alt="Start a Conversation"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default BusinessApplications;
